import { parse } from "@climaterisk/papaparse";
import { z } from "zod";
import { SEVERITIES_TEMPLATE_HEADERS, SEVERITIES_TEMPLATE_KEYS as keys } from "../constants";

/** Row errors listed before validation stops reading the file. */
const MAX_ROW_ERRORS = 10;

const REQUIRED_HEADERS = [keys.latitude, keys.longitude];

// dynamicTyping turns blank cells into null, which should read as missing.
const blankAsMissing = (value: unknown) => (value === null ? undefined : value);

const coordinate = (label: string, limit: number) =>
  z.preprocess(
    blankAsMissing,
    z
      .number({ required_error: `${label} is required.`, invalid_type_error: `${label} must be a number.` })
      .min(-limit, `${label} must be between -${limit} and ${limit}.`)
      .max(limit, `${label} must be between -${limit} and ${limit}.`)
  );

/** A row keyed by template header, parsed to plain field names. */
const rowSchema = z
  .object({
    [keys.id]: z
      .union([z.string(), z.number()])
      .nullish()
      .transform((id) => (id === null || id === undefined ? undefined : String(id).trim() || undefined)),
    [keys.latitude]: coordinate("Latitude", 90),
    [keys.longitude]: coordinate("Longitude", 180),
    [keys.elevation]: z.preprocess(
      blankAsMissing,
      z.number({ invalid_type_error: "Elevation must be a number." }).optional()
    ),
  })
  .transform((row) => ({
    user_supplied_id: row[keys.id],
    latitude: row[keys.latitude],
    longitude: row[keys.longitude],
    elevation: row[keys.elevation],
  }));

export type SeveritiesRow = z.infer<typeof rowSchema>;

export type SeveritiesCsvResult =
  | { status: "success"; rows: SeveritiesRow[] }
  | { status: "error"; messages: string[] };

/** Parses an upload against the Severities template: optional unique IDs, required valid coordinates, optional elevation. */
export const parseSeveritiesCsv = (input: File | string, maxRows: number): Promise<SeveritiesCsvResult> =>
  new Promise((resolve) => {
    const rows: SeveritiesRow[] = [];
    const messages: string[] = [];
    const seenIds = new Set<string>();
    const duplicateIds = new Set<string>();
    let rowCount = 0;

    parse<Record<string, unknown>>(input, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.replace(/^\uFEFF/, "").trim(),
      step: ({ data }, parser) => {
        rowCount++;

        if (rowCount === 1) {
          const headers = Object.keys(data);
          const invalidHeaders = headers.filter((header) => !SEVERITIES_TEMPLATE_HEADERS.includes(header));
          const missing = REQUIRED_HEADERS.filter((header) => !headers.includes(header));

          if (invalidHeaders.length || missing.length) {
            messages.push(
              ...invalidHeaders.map((header) => `Invalid header '${header}'. Please use the headers in the template.`),
              ...missing.map((header) => `Missing required column '${header}'.`)
            );
            parser.abort();
            return;
          }
        }

        if (rowCount > maxRows) {
          messages.push(`A run can't have more than ${maxRows.toLocaleString()} rows.`);
          parser.abort();
          return;
        }

        const result = rowSchema.safeParse(data);
        // Row 1 is the header.
        const label = `Row ${rowCount + 1}`;

        if (!result.success) {
          messages.push(...result.error.issues.map(({ message }) => `${label}: ${message}`));
          if (messages.length >= MAX_ROW_ERRORS) parser.abort();
          return;
        }

        const id = result.data.user_supplied_id;
        if (id !== undefined) {
          if (seenIds.has(id)) duplicateIds.add(id);
          seenIds.add(id);
        }

        rows.push(result.data);
      },
      complete: () => {
        if (!messages.length && !rows.length) messages.push("The file has no rows.");
        if (duplicateIds.size) {
          const listed = [...duplicateIds].slice(0, MAX_ROW_ERRORS).join(", ");
          messages.push(`IDs must be unique. Duplicated: ${listed}${duplicateIds.size > MAX_ROW_ERRORS ? ", …" : ""}`);
        }

        resolve(messages.length ? { status: "error", messages } : { status: "success", rows });
      },
      error: (error) => resolve({ status: "error", messages: [error.message] }),
    });
  });
